import type { FC, ReactElement, ReactNode } from 'react'

import {
  ChildMenu,
  DropdownWrapper,
  PickerPopover,
} from 'features/charts-v2/components/ChartTypePicker/chart-type-picker.styles'
import { useChartTypePicker } from 'features/charts-v2/components/ChartTypePicker/use-chart-type-picker'
import { ParentMenuList } from 'features/charts-v2/components/ParentMenuList/ParentMenuList'
import { PresetMenuItem } from 'features/charts-v2/components/PresetMenuItem/PresetMenuItem'
import type {
  ChartPreset,
  ChartPresetGroup,
  ChartTypeV2,
} from 'features/charts-v2/types/chart-types'
import { useEscapeDismiss } from 'hooks/use-escape-dismiss'

export interface ChartTypePickerSectionItem {
  id: string
  label: string
  description: string
  icon: ReactNode
  /** True when this item IS the tile's current type. */
  isSelected?: boolean
}

/**
 * A caller-supplied parent section — e.g. dashboards-v2-1's "Widgets:"
 * block. Vendor-neutral on purpose: charts-v2 has no concept of a dashboard
 * widget, so the section is entirely caller-supplied data plus a
 * caller-supplied `onSelect`, keeping this shared picker ignorant of what a
 * "widget" even is.
 */
export interface ChartTypePickerSection {
  key: string
  heading: string
  label: string
  icon: ReactNode
  items: readonly ChartTypePickerSectionItem[]
  onSelect: (id: string) => void
  /** True when this section IS the tile's current type — mirrors
   *  `currentPresetGroup` for the built-in sections. Highlights the parent
   *  row the same way a matching preset group does. */
  isSelected?: boolean
}

/** The picker's two built-in blocks. `'templates'` is the "One-click
 *  templates:" heading plus every non-freeform preset group; `'freeform'`
 *  is the "Custom chart:" heading plus its single row. */
export type ChartTypePickerBuiltInSection = 'templates' | 'freeform'

/** One declared section, in render order: a built-in id, or a
 *  caller-supplied section object. Mirrors `ControlsSidebarSection`. */
export type ChartTypePickerSectionSpec =
  | ChartTypePickerBuiltInSection
  | ChartTypePickerSection

export interface ChartTypePickerRenderState {
  isOpen: boolean
  currentPreset: ChartPreset | null
  currentPresetGroup: ChartPresetGroup | null
}

export interface ChartTypePickerProps {
  // Mandatory — every caller declares exactly which sections it wants, in
  // the order they should render. There is no default: `/charts` says
  // `['templates', 'freeform']` explicitly, same as a caller that also wants
  // a caller-supplied section appended.
  sections: readonly ChartTypePickerSectionSpec[]
  // Optional — a tile with no chart at all (a dashboard widget/text tile)
  // has no chart type to resolve a selected preset from.
  selectedChartType?: ChartTypeV2
  selectedPresetId?: string | null
  onChartTypeChange: (chartType: ChartTypeV2) => void
  onPresetSelect?: (preset: ChartPreset | null) => void
  // The element it returns IS the popover's trigger, so it must be a button
  // that forwards `ref` and spreads unknown props onto its DOM node.
  trigger: (state: ChartTypePickerRenderState) => ReactElement
}

/**
 * The charts-v2 chart-type picker dropdown — "One-click templates:" grouped
 * by intent, "Custom chart: → Freeform", and any caller-supplied sections,
 * in the order `sections` declares. Trigger-agnostic: the caller renders its
 * own trigger element via the `trigger` render prop, so `ChartTypeSelector`'s
 * labeled button and dashboards-v2-1's "+ Add content" bar can share this
 * one dropdown body. State, derived lookups, and handlers live in the
 * co-located `useChartTypePicker` hook; the parent-row list and the submenu
 * row are their own co-located components, so this file is pure top-level
 * layout.
 */
export const ChartTypePicker: FC<ChartTypePickerProps> = ({
  sections,
  selectedChartType,
  selectedPresetId,
  onChartTypeChange,
  onPresetSelect,
  trigger,
}) => {
  const {
    showDropdown,
    setShowDropdown,
    activeGroup,
    setActiveGroup,
    childMenuOffset,
    shouldFlipChildMenu,
    parentMenuRef,
    childMenuRef,
    currentPreset,
    currentPresetGroup,
    visibleGroups,
    activeGroupPresets,
    activeCustomItems,
    handleGroupHover,
    handlePresetClick,
    handleCustomItemClick,
  } = useChartTypePicker({
    sections,
    selectedChartType,
    selectedPresetId,
    onChartTypeChange,
    onPresetSelect,
  })

  // The shared Escape stack, so one Escape closes only the innermost overlay.
  useEscapeDismiss({
    isOpen: showDropdown,
    onClose: () => {
      setShowDropdown(false)
      setActiveGroup(null)
    },
  })

  return (
    <PickerPopover
      content={
        <DropdownWrapper>
          <ParentMenuList
            parentMenuRef={parentMenuRef}
            sections={sections}
            visibleGroups={visibleGroups}
            activeGroup={activeGroup}
            currentPresetGroup={currentPresetGroup}
            onGroupHover={handleGroupHover}
          />
          {activeGroup && (activeGroupPresets || activeCustomItems) && (
            <ChildMenu
              ref={childMenuRef}
              $offsetTop={childMenuOffset}
              $flip={shouldFlipChildMenu}
            >
              {activeGroupPresets?.map((preset) => (
                <PresetMenuItem
                  key={preset.id}
                  icon={preset.icon}
                  label={preset.label}
                  description={preset.description}
                  chiclets={preset.chiclets}
                  onClick={() => preset.enabled && handlePresetClick(preset)}
                  disabled={!preset.enabled}
                  isSelected={preset.id === currentPreset?.id}
                />
              ))}
              {activeCustomItems?.map((item) => (
                <PresetMenuItem
                  key={item.id}
                  icon={item.icon}
                  label={item.label}
                  description={item.description}
                  chiclets={[]}
                  onClick={() => handleCustomItemClick(item.id)}
                  isSelected={item.isSelected}
                />
              ))}
            </ChildMenu>
          )}
        </DropdownWrapper>
      }
      side="bottom"
      align="start"
      aria-label="Chart type picker"
      open={showDropdown}
      onOpenChange={(open) => {
        setShowDropdown(open)
        if (!open) setActiveGroup(null)
      }}
    >
      {trigger({
        isOpen: showDropdown,
        currentPreset,
        currentPresetGroup,
      })}
    </PickerPopover>
  )
}
